import { useState } from 'react';

import classNames from 'classnames';

import type { IconName } from '@iconik-io/uad';

import { IS_NAVBAR_EXPANDED } from '../../constants/local_storage';
import IconButton from '../../ui/icon_button/icon_button';
import type { BadgeType } from './badge';
import SidebarMenuButton from './sidebar_menu_button';

import styles from './sidebar_menu.module.css';

export type SidebarMenuItemType = {
  active?: boolean;
  iconName: IconName;
  title: string;
  key: string;
  parent?: string;
  className?: string;
  color?: string;
  disabled?: boolean;
  bottom?: boolean;
  widget?: boolean;
  disabledTooltipText?: string;
  badge?: BadgeType;
  badgeColor?: string;
  badgeLabel?: string;
};

type SidebarMenuProps = {
  className?: string;
  items: SidebarMenuItemType[];
  expandable?: boolean;
  onItemClick?: (item: SidebarMenuItemType) => void;
};

type MenuItemsListProps = {
  allItems: SidebarMenuItemType[];
  items: SidebarMenuItemType[];
  isExpanded?: boolean;
  onItemClick?: (item: SidebarMenuItemType) => void;
};

function MenuItemsList({
  allItems,
  items,
  isExpanded,
  onItemClick,
}: MenuItemsListProps) {
  return (
    <ul className={styles.list}>
      {items.map((item) => {
        const childItems = allItems.filter(
          (potentialChildItem) => potentialChildItem.parent === item.key,
        );

        const isParentItem = allItems.some(
          (itemSub) => itemSub.parent === item.key,
        );

        return (
          <li
            key={item.key}
            className={classNames(
              styles.item,
              {
                [styles.group]: childItems.length,
                [styles.active]: item.active,
              },
              item.className,
            )}
          >
            <SidebarMenuButton
              item={item}
              onItemClick={onItemClick}
              isParentItem={isParentItem}
              isExpanded={isExpanded}
            />
            {item.active && childItems.length ? (
              <MenuItemsList
                allItems={allItems}
                items={childItems}
                isExpanded={isExpanded}
                onItemClick={onItemClick}
              />
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

function SidebarMenu({
  items,
  className,
  expandable,
  onItemClick,
}: SidebarMenuProps) {
  const isNavBarExpanded = localStorage.getItem(IS_NAVBAR_EXPANDED);

  const [isExpanded, setIsExpanded] = useState(
    expandable && isNavBarExpanded !== 'false',
  );

  if (items.length === 0) {
    return null;
  }

  return (
    <div className={classNames(className, styles.menu)}>
      <div
        className={classNames(styles.nav)}
        role="complementary"
        aria-label="Sidebar menu"
      >
        <ul className={styles.wrapper_list}>
          <li>
            <MenuItemsList
              allItems={items}
              items={items.filter((item) => !item.parent && !item.bottom)}
              isExpanded={isExpanded}
              onItemClick={onItemClick}
            />
          </li>
          <li>
            <MenuItemsList
              allItems={items}
              items={items.filter((item) => !item.parent && item.bottom)}
              isExpanded={isExpanded}
              onItemClick={onItemClick}
            />
          </li>
        </ul>
      </div>
      {expandable && (
        <IconButton
          className={classNames(styles.arrow_button, {
            [styles.arrow_button_expanded]: isExpanded,
          })}
          iconName="chevron_small_isolated_e"
          onClick={() => {
            localStorage.setItem(IS_NAVBAR_EXPANDED, (!isExpanded).toString());

            setIsExpanded(!isExpanded);
          }}
        />
      )}
    </div>
  );
}

export default SidebarMenu;
